import { useEffect, useRef } from 'react';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist/legacy/build/pdf.mjs';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import pdfWorker from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url';

GlobalWorkerOptions.workerSrc = pdfWorker;

interface PdfPagePreviewProps {
  url: string;
  title: string;
}

export function PdfPagePreview({ url, title }: PdfPagePreviewProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    let cancelled = false;
    let pdf: PDFDocumentProxy | null = null;
    let renderTask: RenderTask | null = null;
    const loadingTask = getDocument({ url });

    const paint = async (doc: PDFDocumentProxy) => {
      const page = await doc.getPage(1);
      if (cancelled) return;
      const base = page.getViewport({ scale: 1 });
      const dpr = window.devicePixelRatio || 1;
      const height = wrap.clientHeight || 1;
      const viewport = page.getViewport({ scale: (height / base.height) * dpr });
      canvas.width = Math.max(1, Math.floor(viewport.width));
      canvas.height = Math.max(1, Math.floor(viewport.height));
      canvas.style.height = `${height}px`;
      canvas.style.width = `${Math.max(1, Math.floor(viewport.width / dpr))}px`;
      renderTask?.cancel();
      const task = page.render({
        canvas,
        viewport,
        background: 'rgb(255,255,255)',
      });
      renderTask = task;
      try {
        await task.promise;
      } catch (err) {
        if (cancelled || (err as { name?: string }).name === 'RenderingCancelledException') return;
        console.error('pdf render failed', err);
      }
    };

    (async () => {
      try {
        pdf = await loadingTask.promise;
        if (cancelled) return;
        await paint(pdf);
      } catch (err) {
        if (!cancelled) console.error('pdf preview failed', err);
      }
    })();

    const ro = new ResizeObserver(() => {
      if (pdf && !cancelled) void paint(pdf);
    });
    ro.observe(wrap);

    return () => {
      cancelled = true;
      renderTask?.cancel();
      ro.disconnect();
      void pdf?.cleanup();
      void loadingTask.destroy();
    };
  }, [url]);

  return (
    <div
      ref={wrapRef}
      className="flex size-full items-center justify-center overflow-hidden bg-white"
      aria-label={title}
    >
      <canvas ref={canvasRef} className="h-full max-w-full bg-white" />
    </div>
  );
}
